import { mPerLat, mPerLon } from './geo';
// DXF (ASCII) import -> WGS84 vector overlay. CRS: wgs84 | lv95 | lv03 | utm<zone>
const rad = Math.PI / 180;

function lv95ToWgs(e, n) {
  const y = (e - 2600000) / 1e6, x = (n - 1200000) / 1e6;
  const lon = 2.6779094 + 4.728982 * y + 0.791484 * y * x + 0.1306 * y * x * x - 0.0436 * y * y * y;
  const lat = 16.9023892 + 3.238272 * x - 0.270978 * y * y - 0.002528 * x * x - 0.0447 * y * y * x - 0.014 * x * x * x;
  return [(lat * 100) / 36, (lon * 100) / 36];
}

function utmToWgs(E, N, zone) {
  const a = 6378137, f = 1 / 298.257223563, k0 = 0.9996, e2 = f * (2 - f), e1 = (1 - Math.sqrt(1 - e2)) / (1 + Math.sqrt(1 - e2)), ep2 = e2 / (1 - e2);
  const x = E - 500000, M = N / k0, mu = M / (a * (1 - e2 / 4 - (3 * e2 * e2) / 64 - (5 * e2 ** 3) / 256));
  const p1 = mu + (1.5 * e1 - (27 * e1 ** 3) / 32) * Math.sin(2 * mu) + ((21 * e1 * e1) / 16 - (55 * e1 ** 4) / 32) * Math.sin(4 * mu) + ((151 * e1 ** 3) / 96) * Math.sin(6 * mu);
  const C1 = ep2 * Math.cos(p1) ** 2, T1 = Math.tan(p1) ** 2, N1 = a / Math.sqrt(1 - e2 * Math.sin(p1) ** 2), R1 = (a * (1 - e2)) / (1 - e2 * Math.sin(p1) ** 2) ** 1.5, D = x / (N1 * k0);
  const lat = p1 - ((N1 * Math.tan(p1)) / R1) * (D * D / 2 - ((5 + 3 * T1 + 10 * C1 - 4 * C1 * C1 - 9 * ep2) * D ** 4) / 24 + ((61 + 90 * T1 + 298 * C1 + 45 * T1 * T1 - 252 * ep2 - 3 * C1 * C1) * D ** 6) / 720);
  const lon = (D - ((1 + 2 * T1 + C1) * D ** 3) / 6 + ((5 - 2 * C1 + 28 * T1 - 3 * C1 * C1 + 8 * ep2 + 24 * T1 * T1) * D ** 5) / 120) / Math.cos(p1);
  return [lat / rad, (zone - 1) * 6 - 180 + 3 + lon / rad];
}

export const toWgs = (crs, x, y, loc) => {
  if (crs === 'wgs84') return [y, x];
  if (crs === 'local') { const [la, lo] = loc.anchor, s = loc.scale || 1; return [la + ((y - loc.origin[1]) * s) / mPerLat(la), lo + ((x - loc.origin[0]) * s) / (mPerLon(la))]; }
  if (crs === 'lv95') return lv95ToWgs(x, y);
  if (crs === 'lv03') return lv95ToWgs(x + 2000000, y + 1000000);
  return utmToWgs(x, y, +crs.replace('utm', '') || 32);
};

export function guessCrs(sample, near, allowLocal = false) {
  const [x, y] = sample || [0, 0];
  if (Math.abs(x) <= 180 && Math.abs(y) <= 90) return 'wgs84';
  if (x > 2.4e6 && x < 2.9e6 && y > 1.0e6 && y < 1.4e6) return 'lv95';
  if (x > 4.0e5 && x < 9.0e5 && y > 0.5e5 && y < 3.5e5 && near && near[1] > 5.5 && near[1] < 11) return 'lv03';
  if (allowLocal && Math.abs(x) < 1e6 && Math.abs(y) < 1e6) return 'local';
  return `utm${near ? Math.floor((near[1] + 180) / 6) + 1 : 32}`;
}

// minimal group-code parser: LINE, LWPOLYLINE, POLYLINE/VERTEX, POINT, CIRCLE, TEXT/MTEXT, INSERT(point)
// ARC / ELLIPSE / SPLINE -> polyline (DXF: arc angles in degrees, ellipse parameters in radians)
const arcPts = (cx, cy, r, a0, a1) => {
  let e = a1;
  while (e <= a0) e += 2 * Math.PI;
  const n = Math.max(8, Math.ceil(((e - a0) / (2 * Math.PI)) * 48));
  return Array.from({ length: n + 1 }, (_, i) => [cx + r * Math.cos(a0 + ((e - a0) * i) / n), cy + r * Math.sin(a0 + ((e - a0) * i) / n)]);
};
function curvePts(c) {
  const g = (k, d = 0) => (c.g[k] ?? d);
  if (c.kind === 'ARC') return arcPts(g('10'), g('20'), g('40'), g('50') * (Math.PI / 180), g('51', 360) * (Math.PI / 180));
  if (c.kind === 'SPLINE') return c.fp.length > 1 ? c.fp : c.cp;
  const mx = g('11'), my = g('21'), R = Math.hypot(mx, my), rot = Math.atan2(my, mx), r2 = R * g('40', 1), a0 = g('41');
  let a1 = g('42', 2 * Math.PI);
  while (a1 <= a0) a1 += 2 * Math.PI;
  return Array.from({ length: 49 }, (_, i) => { const a = a0 + ((a1 - a0) * i) / 48, x = R * Math.cos(a), y = r2 * Math.sin(a); return [g('10') + x * Math.cos(rot) - y * Math.sin(rot), g('20') + x * Math.sin(rot) + y * Math.cos(rot)]; });
}

export function parseDxf(txt) {
  const L = txt.split(/\r?\n/);
  const pairs = [];
  for (let i = 0; i + 1 < L.length; i += 2) pairs.push([L[i].trim(), L[i + 1].trim()]);
  const s = pairs.findIndex(([c, v]) => c === '2' && v === 'ENTITIES');
  if (s < 0) throw new Error('dxf');
  const ents = [];
  let cur = null, poly = null;
  const flush = () => { if (cur) ents.push(cur.type === 'curve' ? { type: 'line', layer: cur.layer, pts: curvePts(cur) } : cur); cur = null; };
  for (let i = s + 1; i < pairs.length; i++) {
    const [c, v] = pairs[i];
    if (c === '0') {
      if (v === 'ENDSEC') { flush(); break; }
      if (v === 'VERTEX' && poly) { cur = null; poly.cur = [0, 0]; poly.pts.push(poly.cur); continue; }
      if (v === 'SEQEND' && poly) { ents.push(poly.e); poly = null; continue; }
      flush();
      if (v === 'POLYLINE') { poly = { e: { type: 'line', layer: '0', pts: [], closed: false }, pts: [], cur: null }; poly.e.pts = poly.pts; continue; }
      cur = { LINE: { type: 'line', pts: [[0, 0], [0, 0]] }, LWPOLYLINE: { type: 'line', pts: [], lw: true }, POINT: { type: 'point', p: [0, 0] }, INSERT: { type: 'point', p: [0, 0] }, CIRCLE: { type: 'circle', p: [0, 0], r: 0 }, TEXT: { type: 'text', p: [0, 0], text: '' }, MTEXT: { type: 'text', p: [0, 0], text: '' } }[v] || (['ARC', 'ELLIPSE', 'SPLINE'].includes(v) ? { type: 'curve', kind: v, g: {}, cp: [], fp: [] } : null);
      if (cur) cur.layer = '0';
      continue;
    }
    if (poly && !cur) {
      if (c === '8' && !poly.cur) poly.e.layer = v;
      if (c === '70' && !poly.cur) poly.e.closed = (+v & 1) === 1;
      if (poly.cur && c === '10') poly.cur[0] = +v;
      if (poly.cur && c === '20') poly.cur[1] = +v;
      continue;
    }
    if (!cur) continue;
    if (c === '8') cur.layer = v;
    else if (cur.type === 'curve') {
      const arr = cur.kind === 'SPLINE' && (c === '10' || c === '20' ? cur.cp : c === '11' || c === '21' ? cur.fp : null);
      if (arr && c[0] === '1') arr.push([+v, 0]);
      else if (arr && arr.length) arr[arr.length - 1][1] = +v;
      else if (!(c in cur.g)) cur.g[c] = +v;
    } else if (cur.type === 'line' && cur.pts.length === 2 && !cur.lw && (c === '10' || c === '20' || c === '11' || c === '21')) cur.pts[c[1] === '0' ? 0 : 1][c[0] === '1' ? 0 : 1] = +v;
    else if (cur.type === 'line' && (c === '90')) { cur.lw = true; cur.pts = []; }
    else if (cur.type === 'line' && c === '70') cur.closed = (+v & 1) === 1;
    else if (cur.type === 'line' && cur.lw && c === '10') cur.pts.push([+v, 0]);
    else if (cur.type === 'line' && cur.lw && c === '20' && cur.pts.length) cur.pts[cur.pts.length - 1][1] = +v;
    else if (cur.p && c === '10') cur.p[0] = +v;
    else if (cur.p && c === '20') cur.p[1] = +v;
    else if (cur.type === 'circle' && c === '40') cur.r = +v;
    else if (cur.type === 'text' && (c === '1' || c === '3')) cur.text += v.replace(/\\P/g, ' ').replace(/\{|\}|\\[A-Za-z][^;]*;/g, '');
  }
  return ents.filter((e) => (e.type === 'line' ? e.pts.length > 1 && e.pts.every(([a, b]) => Number.isFinite(a) && Number.isFinite(b)) : true)).map(({ lw, ...e }) => e);
}

// local drawings (no geo-reference): bbox centre is placed at the anchor (map centre), units -> metres
export function localMeta(ents, anchor, scale = 1) {
  const P = ents.flatMap((e) => e.pts || [e.p]), xs = P.map((p) => p[0]), ys = P.map((p) => p[1]);
  return { anchor, scale, origin: [(Math.min(...xs) + Math.max(...xs)) / 2, (Math.min(...ys) + Math.max(...ys)) / 2] };
}

export function dxfToWgs(ents, crs, loc) {
  const T = ([x, y]) => toWgs(crs, x, y, loc);
  return ents.map((e) => {
    if (e.type === 'line') return { ...e, pts: (e.closed ? [...e.pts, e.pts[0]] : e.pts).map(T) };
    if (e.type === 'circle') { const c = e.p; return { type: 'line', layer: e.layer, pts: Array.from({ length: 25 }, (_, i) => T([c[0] + e.r * Math.cos((i / 24) * 2 * Math.PI), c[1] + e.r * Math.sin((i / 24) * 2 * Math.PI)])) }; }
    return { ...e, p: T(e.p) };
  }).filter((e) => (e.pts || [e.p]).every(([a, b]) => Number.isFinite(a) && Number.isFinite(b) && Math.abs(a) <= 90 && Math.abs(b) <= 180));
}

const lc = (lat) => mPerLon(lat);
// nearest CAD vertex within r metres
export function snapCad(cad, ll, r = 1) {
  if (!cad || cad.hidden) return null;
  let best = null;
  const off = new Set(cad.off || []);
  cad.ents.filter((e) => !off.has(e.layer)).forEach((e) => (e.pts || [e.p]).forEach((p) => {
    const d = Math.hypot((p[0] - ll[0]) * mPerLat(ll[0]), (p[1] - ll[1]) * lc(ll[0]));
    if (d <= r && (!best || d < best.d)) best = { p, d };
  }));
  return best?.p || null;
}

// distance (m) from point to polylines
export function distToLines(ll, lines) {
  let best = Infinity;
  const k = lc(ll[0]);
  lines.forEach((ln) => {
    for (let i = 0; i + 1 < ln.length; i++) {
      const ax = (ln[i][1] - ll[1]) * k, ay = (ln[i][0] - ll[0]) * mPerLat(ll[0]), bx = (ln[i + 1][1] - ll[1]) * k, by = (ln[i + 1][0] - ll[0]) * mPerLat(ll[0]);
      const dx = bx - ax, dy = by - ay, L2 = dx * dx + dy * dy;
      const u = L2 ? Math.max(0, Math.min(1, -(ax * dx + ay * dy) / L2)) : 0;
      best = Math.min(best, Math.hypot(ax + u * dx, ay + u * dy));
    }
  });
  return best;
}
